/**
 * Fur & Far product media carousel.
 * Turns [data-product-gallery] into a horizontal, swipeable carousel with a
 * large main image, prev/next arrows, and a clickable thumbnail strip.
 * Progressive enhancement: without JS the viewport is still a native
 * horizontal scroller and the thumbnails act as in-page anchors.
 */
(() => {
  const init = (gallery) => {
    const viewport = gallery.querySelector('[data-gallery-viewport]');
    if (!viewport) return;

    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    const prevButton = gallery.querySelector('[data-gallery-prev]');
    const nextButton = gallery.querySelector('[data-gallery-next]');

    if (slides.length <= 1) return;

    const lastIndex = slides.length - 1;
    let current = 0;

    const clamp = (index) => Math.max(0, Math.min(index, lastIndex));

    const pauseVideos = (keep) => {
      gallery.querySelectorAll('video').forEach((video) => {
        if (video !== keep) video.pause();
      });
    };

    const playActiveVideo = () => {
      const video = slides[current]?.querySelector('video');
      pauseVideos(video);
      if (!video) return;
      video.muted = true;
      video.play().catch(() => {});
    };

    const setActive = (index) => {
      current = clamp(index);
      thumbs.forEach((thumb, i) => {
        const isActive = i === current;
        thumb.setAttribute('aria-current', String(isActive));
        if (isActive) {
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      });
      if (prevButton) prevButton.disabled = current === 0;
      if (nextButton) nextButton.disabled = current === lastIndex;
      const setCaption = gallery.querySelector('[data-gallery-set-caption]');
      if (setCaption) setCaption.hidden = !slides[current]?.hasAttribute('data-bowl-set');
      playActiveVideo();
    };

    const goTo = (index, smooth = true) => {
      const target = clamp(index);
      // Every slide is exactly one viewport wide, so scroll by whole viewports.
      viewport.scrollTo({
        left: target * viewport.clientWidth,
        behavior: smooth ? 'smooth' : 'auto',
      });
      setActive(target);
    };

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        goTo(i);
      });
    });

    if (prevButton) prevButton.addEventListener('click', () => goTo(current - 1));
    if (nextButton) nextButton.addEventListener('click', () => goTo(current + 1));

    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(current + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(current - 1);
      }
    });

    // Keep the active thumbnail/arrow state in sync when the shopper swipes or
    // drag-scrolls the main image directly.
    let scrollFrame = null;
    viewport.addEventListener(
      'scroll',
      () => {
        if (scrollFrame) return;
        scrollFrame = requestAnimationFrame(() => {
          scrollFrame = null;
          if (!viewport.clientWidth) return;
          const nearest = clamp(Math.round(viewport.scrollLeft / viewport.clientWidth));
          if (nearest !== current) setActive(nearest);
        });
      },
      { passive: true }
    );

    setActive(0);
  };

  const boot = () => {
    document.querySelectorAll('[data-product-gallery]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
